// Running cook timers. State math is in logic/timers.ts; this store also keeps the OS
// notification in sync (scheduled at endsAt, cancelled on pause/cancel).
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid/non-secure';
import { persistStorage } from './storage';
import { addTime, pauseTimer, resumeTimer, startTimer, type CookTimer } from '../logic/timers';
import { cancelAlarm, scheduleAlarm } from '../platform/notifications';
import i18n from '../i18n';

type Actions = {
  start: (label: string, recipeId: string, stepId: string, seconds: number) => void;
  pause: (id: string) => void;
  resume: (id: string) => void;
  addMinute: (id: string) => void;
  cancel: (id: string) => void;
};

async function arm(t: CookTimer): Promise<string | undefined> {
  if (t.endsAt === null) return undefined;
  try {
    return await scheduleAlarm(t.endsAt, i18n.t('timers.doneTitle', { label: t.label }), i18n.t('timers.doneBody'));
  } catch {
    return undefined;
  }
}

export const useTimers = create<{ timers: CookTimer[] } & Actions>()(
  persist(
    (set, get) => {
      const replace = (t: CookTimer) => set(s => ({ timers: s.timers.map(x => (x.id === t.id ? t : x)) }));
      const rearm = async (before: CookTimer | undefined, after: CookTimer) => {
        if (before?.notificationId) await cancelAlarm(before.notificationId).catch(() => {});
        const notificationId = await arm(after);
        replace({ ...after, notificationId });
      };
      return {
        timers: [],
        start: (label, recipeId, stepId, seconds) => {
          const t = startTimer({ id: nanoid(), label, recipeId, stepId }, seconds * 1000, Date.now());
          set(s => ({ timers: [...s.timers, t] }));
          rearm(undefined, t);
        },
        pause: id => {
          const before = get().timers.find(t => t.id === id);
          if (!before) return;
          const after = pauseTimer(before, Date.now());
          replace(after);
          if (before.notificationId) cancelAlarm(before.notificationId).catch(() => {});
        },
        resume: id => {
          const before = get().timers.find(t => t.id === id);
          if (before) rearm(before, resumeTimer(before, Date.now()));
        },
        addMinute: id => {
          const before = get().timers.find(t => t.id === id);
          if (!before) return;
          const after = addTime(before, 60_000, Date.now());
          if (after.endsAt === null) replace(after);
          else rearm(before, after);
        },
        cancel: id => {
          const before = get().timers.find(t => t.id === id);
          if (before?.notificationId) cancelAlarm(before.notificationId).catch(() => {});
          set(s => ({ timers: s.timers.filter(t => t.id !== id) }));
        },
      };
    },
    { name: 'timers', storage: persistStorage, version: 1, partialize: s => ({ timers: s.timers }) },
  ),
);
